import { NODE_COLORS, resolveNodeType } from './nodeConfig';
import {
  NODE_SHAPES,
  type NodeShapeName,
  type NodeType,
  type NodeTypeAppearance,
  type ProjectNode,
  type ProjectSettings,
} from '../types/project';

// ------------------------------------------------------------
// Per-type node appearance (color, size, shape)
// ------------------------------------------------------------

export const NODE_SHAPE_LABELS: Record<NodeShapeName, string> = {
  circle: 'Circle',
  square: 'Square',
  rounded_square: 'Rounded',
  rectangle: 'Rectangle',
  triangle: 'Triangle',
  diamond: 'Diamond',
  hexagon: 'Hexagon',
  pentagon: 'Pentagon',
  octagon: 'Octagon',
  star: 'Star',
  ellipse: 'Ellipse',
  cross: 'Cross',
};

export const MIN_NODE_TYPE_RADIUS = 6;
export const MAX_NODE_TYPE_RADIUS = 40;
export const DEFAULT_NODE_TYPE_RADIUS = 18;
export const DEFAULT_WAYPOINT_RADIUS = 8;

export interface ResolvedNodeAppearance {
  color: string;
  radius: number;
  shape: NodeShapeName;
}

export function defaultNodeTypeRadius(type: NodeType): number {
  return type === 'waypoint' ? DEFAULT_WAYPOINT_RADIUS : DEFAULT_NODE_TYPE_RADIUS;
}

export function clampNodeTypeRadius(radius: number): number {
  return Math.min(MAX_NODE_TYPE_RADIUS, Math.max(MIN_NODE_TYPE_RADIUS, radius));
}

export function normalizeNodeShape(shape?: string | null): NodeShapeName {
  const value = (shape ?? 'circle').toLowerCase();
  if ((NODE_SHAPES as readonly string[]).includes(value)) {
    return value as NodeShapeName;
  }
  return 'circle';
}

export function resolveNodeTypeAppearance(
  type: NodeType,
  settings?: ProjectSettings,
): ResolvedNodeAppearance {
  const overrides = settings?.node_type_appearance?.[type];
  return {
    color: overrides?.color?.trim() ? overrides.color : NODE_COLORS[type],
    radius: clampNodeTypeRadius(overrides?.radius ?? defaultNodeTypeRadius(type)),
    shape: normalizeNodeShape(overrides?.shape),
  };
}

export function resolveNodeAppearance(
  node: ProjectNode,
  settings?: ProjectSettings,
): ResolvedNodeAppearance {
  const type = resolveNodeType(node) as NodeType;
  return resolveNodeTypeAppearance(type, settings);
}

/** Layout-space hit radius for non-circular shapes (conservative bounding circle). */
export function nodeBoundingRadius(shape: NodeShapeName, radius: number): number {
  switch (shape) {
    case 'star':
      return radius * 1.15;
    case 'cross':
      return radius * 1.1;
    case 'rectangle':
      return radius * 1.05;
    case 'ellipse':
      return radius * 1.05;
    default:
      return radius;
  }
}

export function patchNodeTypeAppearance(
  settings: ProjectSettings | undefined,
  type: NodeType,
  patch: Partial<NodeTypeAppearance>,
): ProjectSettings {
  const current = settings?.node_type_appearance ?? {};
  const merged: NodeTypeAppearance = {
    ...current[type],
    ...patch,
  };
  if (merged.radius != null) {
    merged.radius = clampNodeTypeRadius(merged.radius);
  }
  if (merged.shape != null) {
    merged.shape = normalizeNodeShape(merged.shape);
  }
  return {
    ...(settings ?? {}),
    node_type_appearance: {
      ...current,
      [type]: merged,
    },
  };
}
